'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { JSX, MouseEvent } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { Link01Icon, Link01IconSolid } from '@/components/ui/icon/glyphs';
import { getSolscanTxUrl } from '@/lib/solscan';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface SolscanLinkProps {
  signature: string;
  /**
   * Visual variant.
   *
   * - `button` (default): existing 28×28 `<AnimatedButton>` icon button. Used
   *   wherever Solscan sits inside an action bar.
   * - `inline`: 12px solid chain icon as a bare `<a>` for use inside text /
   *   metadata rows (spec 094 — Debate tab + Portfolio Theses panel). No
   *   button chrome, no hover background. Stops propagation on click so the
   *   parent (e.g. a pressable card link) does not also navigate.
   */
  variant?: 'button' | 'inline';
  className?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SolscanLink(t0) {
  const $ = _c(21);
  const {
    signature,
    variant: t1,
    className
  } = t0;
  const variant = t1 === undefined ? "button" : t1;
  const t = useTranslations("social");
  if (!signature) {
    return null;
  }
  let t2;
  if ($[0] !== signature) {
    t2 = getSolscanTxUrl(signature);
    $[0] = signature;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const href = t2;
  let t3;
  if ($[2] !== t) {
    t3 = t("verify_onchain");
    $[2] = t;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const label = t3;
  if (variant === "inline") {
    let t4;
    if ($[4] !== className) {
      t4 = cn("inline-flex shrink-0 items-center text-foreground-tertiary transition-colors hover:text-foreground-secondary", className);
      $[4] = className;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    let t5;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Icon icon={Link01IconSolid} className="size-3" aria-hidden={true} />;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    let t6;
    if ($[7] !== href || $[8] !== label || $[9] !== t4) {
      t6 = <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label} title={label} onClick={_temp} className={t4}>{t5}</a>;
      $[7] = href;
      $[8] = label;
      $[9] = t4;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    return t6;
  }
  let t4;
  if ($[11] !== className) {
    t4 = cn("h-7 min-h-7 w-7 min-w-7 shrink-0 gap-0 p-0 text-foreground-tertiary", className);
    $[11] = className;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Icon icon={Link01Icon} className="size-4 shrink-0 text-foreground-tertiary" aria-hidden={true} />;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== href || $[15] !== label) {
    t6 = <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>{t5}</a>;
    $[14] = href;
    $[15] = label;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== label || $[18] !== t4 || $[19] !== t6) {
    t7 = <AnimatedButton asChild={true} variant="tertiary" size="sm" aria-label={label} title={label} className={t4}>{t6}</AnimatedButton>;
    $[17] = label;
    $[18] = t4;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
}
function _temp(e) {
  e.stopPropagation();
}